import type { Applicant } from '../types';

export type FinancialAidBoardColumnStatus = 'Applied' | 'In-Progress' | 'Scheduled' | 'Decision';

export type FinancialAidBoardStatusTransition =
  | { kind: 'noop' }
  | {
      kind: 'beginReview';
      successMessage: string;
    }
  | {
      kind: 'requestMoreInfo';
      requestedInfo: string;
      deadlineDays: number;
      successMessage: string;
    }
  | { kind: 'unsupported'; reason: string };

const INVITE_STATUS_TOKENS = new Set(['invited', 'opened', 'started', 'invite_sent']);
const SUBMITTED_STATUS_TOKENS = new Set(['submitted', 'applied', 'new']);
const SUBMITTED_STATUS_LABEL_TOKENS = new Set(['submitted', 'new_inquiry']);
const DECISION_BOARD_STATUSES = new Set(['Decision', 'Accepted', 'Declined', 'Denied']);

const readString = (value: unknown): string => String(value ?? '').trim();

const normalizeStatusToken = (status: unknown): string =>
  readString(status)
    .toLowerCase()
    .replace(/[\s-]+/g, '_');

export const getFinancialAidBoardStatusGroup = (
  status: Applicant['status'] | FinancialAidBoardColumnStatus,
): Applicant['status'] | FinancialAidBoardColumnStatus =>
  DECISION_BOARD_STATUSES.has(status) ? 'Decision' : status;

export const isFinancialAidInviteApplicant = (
  applicant: Pick<Applicant, 'rawStatus' | 'statusLabel'>,
): boolean => {
  const rawStatusToken = normalizeStatusToken(applicant.rawStatus);
  if (SUBMITTED_STATUS_TOKENS.has(rawStatusToken)) {
    return false;
  }
  const labelToken = normalizeStatusToken(applicant.statusLabel);
  return INVITE_STATUS_TOKENS.has(rawStatusToken) || INVITE_STATUS_TOKENS.has(labelToken);
};

export const isFinancialAidSubmittedStatus = (
  applicant: Pick<Applicant, 'rawStatus' | 'status' | 'statusLabel'>,
): boolean => {
  const rawStatusToken = normalizeStatusToken(applicant.rawStatus);
  if (INVITE_STATUS_TOKENS.has(rawStatusToken)) return false;
  const statusToken = normalizeStatusToken(applicant.status);
  const labelToken = normalizeStatusToken(applicant.statusLabel);
  return (
    SUBMITTED_STATUS_TOKENS.has(rawStatusToken)
    || SUBMITTED_STATUS_TOKENS.has(statusToken)
    || SUBMITTED_STATUS_LABEL_TOKENS.has(labelToken)
  );
};

export const resolveFinancialAidBoardStatusTransition = (
  applicant: Pick<Applicant, 'id' | 'name' | 'status' | 'rawStatus' | 'statusLabel'>,
  targetStatus: Applicant['status'] | FinancialAidBoardColumnStatus,
): FinancialAidBoardStatusTransition => {
  const currentGroup = getFinancialAidBoardStatusGroup(applicant.status);
  const targetGroup = getFinancialAidBoardStatusGroup(targetStatus as Applicant['status']);

  if (currentGroup === targetGroup) {
    return { kind: 'noop' };
  }

  if (isFinancialAidInviteApplicant(applicant)) {
    return {
      kind: 'unsupported',
      reason:
        'Invite rows cannot be moved between review statuses until the family submits the financial-aid application.',
    };
  }

  const rawStatus = normalizeStatusToken(applicant.rawStatus || applicant.status);

  if (targetGroup === 'In-Progress' && ['submitted', 'applied', 'new'].includes(rawStatus)) {
    return {
      kind: 'beginReview',
      successMessage: `${applicant.name} moved to Under Review.`,
    };
  }

  if (
    targetGroup === 'Scheduled'
    && ['under_review', 'in_progress', 'inprogress', 'submitted', 'applied', 'new'].includes(rawStatus)
  ) {
    return {
      kind: 'requestMoreInfo',
      requestedInfo:
        'Dashboard status move marked this application as needing more information. Staff should follow up with the family with any specific requested documents or clarifications.',
      deadlineDays: 14,
      successMessage: `${applicant.name} moved to More Info Needed.`,
    };
  }

  if (targetGroup === 'Decision') {
    return {
      kind: 'unsupported',
      reason:
        'Use the application detail review actions to approve or deny financial-aid applications.',
    };
  }

  if (targetGroup === 'Applied') {
    return {
      kind: 'unsupported',
      reason:
        'Submitted financial-aid applications cannot be moved back to Applied from the dashboard.',
    };
  }

  return {
    kind: 'unsupported',
    reason: 'This financial-aid status move is not supported by the audited workflow yet.',
  };
};
